import {
    BaseAuthenticationFlow,
    IAuthenticationFlowResult,
    IAutoLoginResponseModel,
    IEnhancedLoginResult,
    IAuthenticationStorage,
} from '../models';
import { LoggerProvider } from '../../logger';
import {
    NavigateLinkId,
    PreloaderComponentType,
    RedirectPageHash,
} from '../../../models/enums/general-enums';
import { UCFData, WindowToken } from '../../../injection-tokens';
import Container from 'typedi';
import { CrossAuthenticationService } from '../services/cross-authentication.service';
import {
    ILogger,
    IOpenTouchidSettingsPageData,
    MessageBroker,
    UiChannel,
} from '@sparkware/uc-sdk-core';
import { UserareaService } from '../../external/userarea';
import { BiometricsUtils } from '../../biometrics/biometrics.utils';
import { CookieStoreItemService } from '../../storage/implementations';
import { UrlUtils } from '../../utils/urlUtils';

import { AuthenticationFlowType } from '../enums';
import { LoaderManager } from '../../../loaders/LoaderManager';
import { IPreloaderManager } from '../../ui/preloader/models/interfaces/IPreloaderManager';

export class CrossAuthenticationFlow extends BaseAuthenticationFlow {
    private readonly _logger: ILogger;
    private readonly _crossAuthenticationService: CrossAuthenticationService;
    private readonly _window: Window;
    private readonly _uiChannel: UiChannel;
    private readonly _ucfData: any;
    private readonly _userAreaService: UserareaService;
    private readonly _biometricsUtils: BiometricsUtils;
    private readonly _cookieStoreItemService: CookieStoreItemService;
    private readonly _urlUtils: UrlUtils;

    public get type(): AuthenticationFlowType {
        return AuthenticationFlowType.Cross;
    }

    private get _authenticationStoragePromise(): Promise<IAuthenticationStorage> {
        return LoaderManager.Instance.AuthenticationStorageLoader.Instance;
    }

    private get _preloaderManagerPromise(): Promise<IPreloaderManager> {
        return LoaderManager.Instance.PreloaderManagerLoader.Instance;
    }

    constructor() {
        super();
        this._logger = Container.get(LoggerProvider).getLogger('CrossAuthenticationFlow');
        this._crossAuthenticationService = Container.get(CrossAuthenticationService);
        this._urlUtils = Container.get(UrlUtils);
        this._window = Container.get(WindowToken);
        this._userAreaService = Container.get(UserareaService);
        this._uiChannel = MessageBroker.getInstance().ui;
        this._ucfData = Container.get(UCFData);
        this._biometricsUtils = Container.get(BiometricsUtils);
        this._cookieStoreItemService = Container.get(CookieStoreItemService);
    }

    start = async (): Promise<IAuthenticationFlowResult> => {
        try {
            const isAutoLoginRequest = this._urlUtils.isAutologinRequest();
            const isNotTouchIdRequest =
                parseInt(this._urlUtils.getQueryStringParameterByName('LinkId') ?? 0) !==
                NavigateLinkId.ToTouchId;
            let response: IEnhancedLoginResult<IAutoLoginResponseModel>, errorResponse: any;

            const biometricsEnabled = this._biometricsUtils.FindIfBiometricsFlowIsEnabled();

            const userSelectedStateQsParam =
                this._urlUtils.getQueryStringParameterByName('userSelectedState');

            if (userSelectedStateQsParam) {
                this._cookieStoreItemService.set('userSelectedState', userSelectedStateQsParam, {});
                this._removeQSParams('userSelectedState');
            }

            if (!isAutoLoginRequest) {
                return {
                    continue: true,
                    description: 'Continue with a fallback since is not an auto login request',
                };
            }

            if (this._redirectToContentLang()) {
                return {
                    continue: false,
                    description: 'Redirection to a different content language.',
                };
            }

            const linkId = this._urlUtils.getLinkId();
            const authenticationStorage = await this._authenticationStoragePromise;
            const preloaderManager = await this._preloaderManagerPromise;
            authenticationStorage.setLoginOptions({
                biometrics: {
                    skipBiometricsEnrollment: biometricsEnabled && !!linkId,
                },
            });

            if (isNotTouchIdRequest) {
                ({ response, errorResponse } = await this._crossAuthenticationService.autoLogin());
            }

            if (linkId && (!!response?.Response?.BearerToken || !isNotTouchIdRequest)) {
                this._handleLinkId(linkId);
                preloaderManager?.onComponentFinished(PreloaderComponentType.RedirectHash, {
                    isLoaded: true,
                });
            }

            preloaderManager?.onComponentFinished(PreloaderComponentType.CrossAutoLogin, {
                isLoaded: true,
            });

            this._removeQSParams();

            return {
                continue: false,
                description: 'Finished cross authentication flow.',
            };
        } catch (err) {
            this._logger.warn(err);
            return {
                continue: true,
                description:
                    'Continue with a fallback since there was an uncaught exception during cross authentication flow',
            };
        }
    };

    private _redirectToContentLang(): boolean {
        const redirectContentLang = this._ucfData.RedirectContentLang?.toLowerCase();
        const currentContentLang = this._window.pageContextManager
            .getLocalizationData()
            .contentLangId?.toLowerCase();

        if (currentContentLang !== redirectContentLang && redirectContentLang) {
            const parsedUrl = new URL(this._window.location.href);
            parsedUrl.searchParams.delete('LangID');
            parsedUrl.searchParams.append('lang', redirectContentLang);

            delete this._ucfData.RedirectContentLang;
            this._urlUtils.redirectToUrl(parsedUrl.toString());

            return true;
        }

        return false;
    }

    private _handleLinkId(linkId: NavigateLinkId) {
        switch (linkId) {
            case NavigateLinkId.ToSportOffers: {
                this._window.location.hash = RedirectPageHash.FreeBets;
                break;
            }
            case NavigateLinkId.ToTouchId: {
                this._userAreaService.executeOnload(() => {
                    this._uiChannel.topics.OpenTouchidSettingsPage.publish(
                        {
                            publisher: 'UCF.RouterService',
                        },
                        {
                            enrollmentMode: false,
                            callbackUrl: null,
                        } as IOpenTouchidSettingsPageData,
                    );
                });

                break;
            }
        }
    }

    private _removeQSParams = (param?: string) => {
        let url = new URL(this._window.location.href);

        if (param) {
            url.searchParams.delete(param);
        } else {
            let copySearchParams = new URLSearchParams(this._window.location.search);
            copySearchParams.forEach((_, key) => {
                if (key !== 'SourceProductPackage') url.searchParams.delete(key);
            });
        }

        this._window.history.replaceState({}, this._window.document.title, url.toString());
    };
}
